import { useState } from 'react';
import { t } from '@lingui/core/macro';
import { Plural } from '@lingui/react/macro';
import { Link } from '@tanstack/react-router';
import Popover from '../../ui/Popover';
import { Note, Tag } from '../../../types/api';
import { useOptionalBookmarkSelection } from '../../../contexts/BookmarkSelectionContext';

interface BookmarkNotesTagsProps {
  notes?: Note[];
  tags?: Tag[];
  bookmarkId: string;
}

function BookmarkNotesTags({ notes, tags, bookmarkId }: BookmarkNotesTagsProps) {
  const highlightCount = notes?.length || 0;
  const hasNotes = highlightCount > 0;
  const hasTags = tags && tags.length > 0;
  const [isNotesPopoverOpen, setIsNotesPopoverOpen] = useState(false);
  const isBatchMode = useOptionalBookmarkSelection()?.isBatchMode ?? false;

  // Don't render anything if there are no notes or tags
  if (!hasNotes && !hasTags) {
    return null;
  }

  return (
    <div className="bookmark-notes-tags">
      {/* Notes/Highlights */}
      {hasNotes && (
        <span
          className="inline-flex items-center"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <Popover
            placement="bottom-start"
            open={isNotesPopoverOpen}
            onOpenChange={setIsNotesPopoverOpen}
            preventCloseOnContentClick
            content={
              <div
                data-testid="bookmark-notes-popover"
                className="max-h-80 w-80 overflow-y-auto px-1"
                onClick={(event) => event.stopPropagation()}
                onMouseLeave={() => setIsNotesPopoverOpen(false)}
              >
                <div className="space-y-1">
                  {notes?.map((note) => {
                    const highlightId = note.highlight_id ?? note.id;
                    return (
                      <Link
                        key={note.id}
                        to="/read/$bookmarkId/$highlightId"
                        params={{
                          bookmarkId: note.bookmark_id || bookmarkId,
                          highlightId,
                        }}
                        className="menu-item h-auto py-2"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <p className="text-copy-primary text-sm leading-snug font-medium">
                          {note.text}
                        </p>
                        {note.note && (
                          <p className="text-copy-secondary mt-1 line-clamp-2 text-xs">
                            {note.note}
                          </p>
                        )}
                      </Link>
                    );
                  })}
                  {!notes?.length && (
                    <p className="text-copy-secondary px-2 py-1 text-xs">{t`No notes available`}</p>
                  )}
                </div>
              </div>
            }
          >
            <button
              type="button"
              className="badge badge-highlight text-bookmark-item-badge"
              title={t`Show notes`}
            >
              <Plural value={highlightCount} one="# note" other="# notes" />
            </button>
          </Popover>
        </span>
      )}

      {/* Tags */}
      {hasTags &&
        tags.map((tag) =>
          isBatchMode ? (
            <span key={tag.id} className="badge badge-tag text-bookmark-item-tag">
              {tag.name}
            </span>
          ) : (
            <Link
              key={tag.id}
              to="/tags/$slug"
              params={{ slug: tag.slug }}
              className="badge badge-tag text-bookmark-item-tag"
            >
              {tag.name}
            </Link>
          ),
        )}
    </div>
  );
}

export default BookmarkNotesTags;
